// GA4 provider — SPA-aware, consent-gated, lazy.
// Loads gtag.js directly (no GTM container). Controls pageviews manually.
// Google Ads conversion (AW-…) is also configured here so the legacy
// `gtag('event','conversion',{send_to:'AW-…'})` calls continue to work.

import { metrics } from "../metrics";
import { CONVERSION_EVENTS } from "../schema";
import { getAttributionContext } from "../utm";
import type { EnrichedEvent, IdentifyTraits, PageInfo } from "../types";
import type { Provider } from "./types";

// IDs are publishable. Hardcoded fallback matches the previous GTM container.
const GA4_MEASUREMENT_ID =
  (import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined) || "G-CMMXTR58CT";
const GOOGLE_ADS_ID =
  (import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined) || "AW-304161929";

function ensureGtagStub(): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  };
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag !== "function") {
    w.gtag = function gtagStub() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments as unknown as Record<string, unknown>);
    };
  }
}

export class GA4Provider implements Provider {
  readonly name = "ga4";
  readonly categories = ["analytics"] as const;
  private loaded = false;
  private lastPath: string | null = null;
  private scriptInjected = false;

  async load(): Promise<void> {
    if (this.loaded) return;
    if (typeof window === "undefined" || !GA4_MEASUREMENT_ID) {
      this.loaded = true;
      return;
    }
    const t0 = performance.now();
    try {
      ensureGtagStub();
      // Inject gtag.js script (async). Idempotent via this.scriptInjected.
      if (!this.scriptInjected) {
        this.scriptInjected = true;
        const s = document.createElement("script");
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
        document.head.appendChild(s);
      }

      window.gtag!("js", new Date());
      // SPA mode: disable automatic pageview, IP anonymization on by default
      // in EEA; explicit here.
      window.gtag!("config", GA4_MEASUREMENT_ID, {
        send_page_view: false,
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
      // Google Ads conversion linker (separate measurement). Required so
      // legacy `trackPurchase` → `gtag('event','conversion',{send_to:'AW-…'})`
      // continues to fire correctly without GTM.
      if (GOOGLE_ADS_ID) {
        window.gtag!("config", GOOGLE_ADS_ID);
      }
      this.loaded = true;
      metrics.providerLoadMs("ga4", Math.round(performance.now() - t0));
    } catch (err) {
      metrics.providerFailure("ga4");
      this.loaded = true;
      // eslint-disable-next-line no-console
      console.warn("[analytics] GA4 load failed:", err);
    }
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  identify(userHash: string): void {
    if (typeof window === "undefined" || !window.gtag) return;
    try {
      window.gtag("config", GA4_MEASUREMENT_ID, { user_id: userHash });
    } catch {
      /* noop */
    }
  }

  page(page: PageInfo): void {
    if (typeof window === "undefined" || !window.gtag) return;
    const path = page.current_path;
    if (path === this.lastPath) return; // dedup
    this.lastPath = path;
    try {
      window.gtag("event", "page_view", {
        page_location: window.location.href,
        page_path: path,
        page_title: page.title || document.title,
      });
    } catch {
      metrics.providerFailure("ga4");
    }
  }

  track(event: EnrichedEvent): void {
    if (typeof window === "undefined" || !window.gtag) return;
    try {
      // page_viewed is handled via page() — skip here to avoid double-fire.
      if (event.name === "page_viewed") return;

      const params: Record<string, unknown> = {
        ...event.payload,
        // Standard GA4 ecommerce-ish fields
        currency: (event.payload as Record<string, unknown>).currency,
        value: (event.payload as Record<string, unknown>).value,
      };

      // Attribution context (first/last touch + click IDs) attached to
      // conversions only — keeps non-conversion events lean.
      if (CONVERSION_EVENTS.has(event.name as never)) {
        const attr = getAttributionContext();
        if (attr.first?.source) params["first_touch_source"] = attr.first.source;
        if (attr.first?.campaign) params["first_touch_campaign"] = attr.first.campaign;
        if (attr.last?.source) params["last_touch_source"] = attr.last.source;
        if (attr.last?.campaign) params["last_touch_campaign"] = attr.last.campaign;
        if (attr.session_utm.gclid) params["gclid"] = attr.session_utm.gclid;
        if (attr.session_utm.fbclid) params["fbclid"] = attr.session_utm.fbclid;
      }

      // Drop undefined to keep payloads clean.
      for (const k of Object.keys(params)) if (params[k] === undefined) delete params[k];

      window.gtag("event", event.name, params);
    } catch {
      metrics.providerFailure("ga4");
    }
  }

  reset(): void {
    // No-op — GA4 client_id rotation is handled via analytics.reset().
  }

  async shutdown(): Promise<void> {
    // Disable further hits without unloading the script (idempotent).
    if (typeof window === "undefined") return;
    try {
      (window as unknown as Record<string, unknown>)[`ga-disable-${GA4_MEASUREMENT_ID}`] = true;
      if (GOOGLE_ADS_ID) {
        (window as unknown as Record<string, unknown>)[`ga-disable-${GOOGLE_ADS_ID}`] = true;
      }
    } catch {
      /* noop */
    }
  }
}
